import React, { useEffect, useMemo, useState } from 'react';
import { EChartsOption } from 'echarts';
import BaseChart from './BaseChart';
import { apiFetch } from '../../services/api/client';
import { DEFAULT_ARTIST_ID } from '../../config/nova-constants';
import { CHART_NO_DATA_FOR_ARTIST } from './chartMessages';

type AgeSlice = {
  age_group: string;
  percentage: number;
};

type InstagramAgeDistributionChartProps = {
  artistId?: string;
  className?: string;
  refreshKey?: number;
  size?: 'compact' | 'full';
};

const colors = ['#0EA5E9', '#22C55E', '#F59E0B', '#8B5CF6', '#F97316', '#14B8A6'];

const InstagramAgeDistributionChart: React.FC<InstagramAgeDistributionChartProps> = ({
  artistId,
  className = '',
  refreshKey = 0,
  size = 'compact'
}) => {
  const [data, setData] = useState<AgeSlice[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        const id = artistId || DEFAULT_ARTIST_ID;
        const response = await apiFetch(`/analytics/artist/instagram-age-distribution?artist_id=${id}`);
        const payload = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
        const normalized = payload
          .map((item: any) => ({
            age_group: item.ageGroup || item.age_group || item.name || '',
            percentage: Number(item.percentage ?? item.value ?? 0)
          }))
          .filter((entry: AgeSlice) => !Number.isNaN(entry.percentage));

        const nonZero = normalized.some((item: AgeSlice) => item.percentage > 0);

        if (mounted) {
          if (normalized.length && nonZero) {
            setData(normalized);
            setHasData(true);
          } else {
            setData([]);
            setHasData(false);
          }
        }
      } catch (_error) {
        if (mounted) {
          setData([]);
          setHasData(false);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => { mounted = false; };
  }, [artistId, refreshKey]);

  const option = useMemo<EChartsOption>(() => {
    const seriesData = data.map((item, idx) => ({
      name: item.age_group,
      value: Number(item.percentage || 0),
      itemStyle: { color: colors[idx % colors.length] }
    }));

    return {
      backgroundColor: '#ffffff',
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: { color: '#1f2937', fontSize: 12 },
        formatter: (params: any) => `
          <div style="font-weight: 600; margin-bottom: 6px; color: #111827;">${params.name}</div>
          <div style="color: ${params.color};">${params.value.toFixed(2)}%</div>
        `
      },
      legend: {
        orient: 'vertical',
        right: 5,
        top: 'center',
        textStyle: { color: '#4b5563', fontSize: 11 },
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 10,
        padding: [8, 5, 8, 5],
        width: 'auto',
        formatter: function(name: string) {
          // Truncate long names if needed
          return name.length > 15 ? name.substring(0, 15) + '...' : name;
        }
      },
      series: [
        {
          name: 'Age',
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['35%', '50%'],
          avoidLabelOverlap: true,
          label: { show: false },
          emphasis: { scale: true, scaleSize: 6 },
          data: seriesData
        }
      ]
    };
  }, [data]);

  const containerStyle: React.CSSProperties = size === 'compact'
    ? { height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }
    : { minHeight: 460 };

  const chartStyle = size === 'compact'
    ? { width: '100%', height: '100%', minHeight: 0, flex: 1 }
    : { width: '100%', height: '100%', maxWidth: 1100, maxHeight: 720 };

  if (loading) {
    return (
      <div className="w-full h-full bg-transparent p-2 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (!hasData || !data.length) {
    return (
      <div className="w-full h-full bg-transparent px-2 pt-2 pb-1 flex items-center justify-center">
        <p className="text-red-400 text-sm">{CHART_NO_DATA_FOR_ARTIST}</p>
      </div>
    );
  }

  return (
    <div
      className={`w-full h-full bg-transparent px-2 pt-2 pb-4 ${className || ''}`}
      style={containerStyle}
    >
      <BaseChart
        option={option}
        className="w-full h-full"
        style={chartStyle}
      />
    </div>
  );
};

export default InstagramAgeDistributionChart;
